// index.ts — entry point for the encounter-map helpers bundle.
//
// esbuild bundles everything reachable from here into
// includes/js/dist/encounter-map-bundle.js. All extracted helpers are namespaced
// under window.FTEncounterMap so DragManager / AOEManager methods can delegate
// to them without polluting the
// global namespace with ~17 individual function names.
//
// Pattern mirrors the three established TS bundles (log / turn-widget /
// encounter-summary) — see docs/template-js-extraction.md for the conventions.

import * as geom     from './grid-math';
import * as drag     from './drag-helpers';
import * as aoe      from './aoe-helpers';
import * as dragCtrl from './drag/index';   // Phase 1.1 sub-controller classes

// Phase 2.1: AOEManager host class moved into the bundle. Import for side
// effects only — the file's bottom-of-module code constructs the singleton
// and attaches it to `window.AOEManager` / `window.aoeManager`.
import './aoe/AOEManager';

// Phase 2.2: DragManager host class moved into the bundle. Same shape —
// bottom-of-module constructs the singleton and attaches `window.DragManager`
// + `window.dragManager`.
import './drag/DragManager';

// Cross-bundle reach capability (2026-07-12): attaches window.ftActiveViewerCanReach so the SEPARATE
// object-card bundle can reach-gate container contents without importing encounter-map internals.
// Side-effect import — the module's bottom attaches the global. See xz/reach-capability.ts.
import './ft3d/reach-capability';

// Combat noise table mirror (owner 2026-08-26): attaches window.ftCombatNoise so the action bar can
// preview how far a hostile action carries. Server (combat_membership.php) stays authoritative.
import './ft3d/combat-noise';

// FT3D floating inventory master + Equipment / On Hand / Bag windows. This used to be owned by the
// retired XZ widget module; the campaign shell and its I binding consume the window API directly.
import './ft3d/ft3d-inventory-windows';

// Scenery/Interior tile pickers for ft3d_scene_host.php (2026-07-07). Side-effect import:
// self-initializes at DOMContentLoaded, no-op on pages without the #ft_scenery_tiles mount.
// Replaced the form's inline scripts + PHP catalog mirrors — picker data now comes straight
// from ft3d-bg-manifest.ts.
import './ft3d/ft3d-scenery-picker';

// Persisted-zone projection for the FT3D iframe (2026-09-09): my_campaign.php's encounter projection
// calls window.ftCollectFt3dPersistentZones(encounterArr) so the 3D runtime can draw every lingering
// pAOE (Wall of Light, Fog Cloud, an aura) with the same renderer the placement preview used.
import { collectFt3dPersistentZones } from './ft3d/persistent-zones';

// Merge Phase 0 pure helpers with Phase 1.x sub-controller classes under their
// respective namespaces. Helpers keep their kebab-case-derived names; controllers
// keep their PascalCase class names — no collisions.
const dragNamespace = { ...drag, ...dragCtrl };
const aoeNamespace = { ...aoe };

declare global {
    interface Window {
        FTEncounterMap: {
            geom: typeof geom;
            drag: typeof dragNamespace;
            aoe:  typeof aoeNamespace;
        };
        ftCollectFt3dPersistentZones: typeof collectFt3dPersistentZones;
    }
}

Object.assign(window, {
    FTEncounterMap: { geom, drag: dragNamespace, aoe: aoeNamespace },
    ftCollectFt3dPersistentZones: collectFt3dPersistentZones,
});
